// src/components/CatechistProfile.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { StudentContext } from '../StudentContext';
import {
  subscribeCatechistProfile,
  updateCatechist,
  createLeaveRequest,
  updateLeaveRequest,
  fetchLeaveRequestsByCatechist,
  subscribeLeaveRequestsByCatechist,
  subscribeCatechistAttendanceByUid,
  subscribeMeetingDates
} from '../FirebaseService';
import PasswordModal from './PasswordModal';
import { auth } from '../firebaseConfig';
import './CatechistProfile.css';

// Cấu hình các trường sơ yếu lý lịch GLV được phép click để sửa
const GLV_EDITABLE_FIELDS = {
  holyName: { label: 'Tên Thánh', type: 'text' },
  fullName: { label: 'Họ và Tên', type: 'text', required: true },
  gender: { label: 'Giới tính', type: 'select', options: ['Nam', 'Nữ'] },
  dateOfBirth: { label: 'Ngày sinh', type: 'date' },
  phone: { label: 'Số điện thoại liên lạc', type: 'tel' },
  email: { label: 'Email cá nhân', type: 'email' },
  address: { label: 'Địa chỉ cư ngụ', type: 'text' },
  profession: { label: 'Nghề nghiệp hiện tại', type: 'text' },
  educationLevel: { label: 'Trình độ học vấn', type: 'text' },
  baptismDate: { label: 'Ngày Rửa Tội', type: 'date' },
  baptismPlace: { label: 'Nơi Rửa Tội (Giáo xứ)', type: 'text' },
  confirmationDate: { label: 'Ngày Thêm Sức', type: 'date' },
  confirmationPlace: { label: 'Nơi Thêm Sức (Giáo xứ)', type: 'text' }
};

const CatechistProfile = () => {
  const navigate = useNavigate();
  const { currentYearId, allBlocks } = useContext(StudentContext);

  const [profile, setProfile] = useState({
    uid: '',
    idCode: '',
    fullName: '',
    holyName: '',
    gender: 'Nam',
    religious: 'Giáo lý viên',
    role: 'catechist',
    username: '',
    className: '',
    blockLeader: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    address: '',
    profession: '',
    educationLevel: '',
    catechistLevel: 'Dự bị',
    baptismDate: '',
    baptismPlace: '',
    confirmationDate: '',
    confirmationPlace: '',
    notes: '',
    isProfileLocked: false
  });

  const [activeTab, setActiveTab] = useState('stats');
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  // 🌟 Mặc định ẩn Sơ Yếu Lý Lịch để giao diện gọn gàng
  const [isExpandedCV, setIsExpandedCV] = useState(false);
  const [error, setError] = useState('');

  // Modal Sửa Nhanh khi GLV bấm trực tiếp vào thông tin
  const [glvEditModal, setGlvEditModal] = useState({
    open: false,
    fieldKey: '',
    value: ''
  });
  const [isSubmittingField, setIsSubmittingField] = useState(false);

  // Điểm danh & Họp
  const [rawAttendanceMap, setRawAttendanceMap] = useState({});
  const [meetingDatesMap, setMeetingDatesMap] = useState({});
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);

  // Đơn xin nghỉ
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [isLoadingLeaveRequests, setIsLoadingLeaveRequests] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);

  const [leaveForm, setLeaveForm] = useState({
    requestDate: '',
    reason: '',
    absentMass: true,
    absentTeaching: true,
    absentMeeting: false
  });

  // Phân loại màu sắc theo Ngành: Chiên Con (Hồng), Ấu (Xanh lá), Thiếu (Xanh dương), Nghĩa (Vàng), Hiệp Sĩ (Nâu)
  const getSectorThemeClass = (text) => {
    if (!text) return 'sector-default';
    const str = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    // 1. Chiên Con - Hồng (Khai Tâm, Tô Màu, Chiên)
    if (str.includes('chien') || str.includes('khai tam') || str.includes('to mau')) {
      return 'sector-chien-con';
    }
    // 2. Ngành Ấu - Xanh lá (Ấu Nhi, Rước Lễ)
    if (str.includes('au') || str.includes('ruoc le')) {
      return 'sector-au';
    }
    // 3. Ngành Thiếu - Xanh dương (Thiếu Nhi, Thêm Sức)
    if (str.includes('thieu') || str.includes('them suc')) {
      return 'sector-thieu';
    }
    // 4. Ngành Nghĩa - Vàng (Nghĩa Sĩ, Bao Đồng)
    if (str.includes('nghia') || str.includes('bao dong')) {
      return 'sector-nghia';
    }
    // 5. Ngành Hiệp Sĩ - Nâu (Hiệp Sĩ, Dự Bị, Vào Đời)
    if (str.includes('hiep') || str.includes('du bi') || str.includes('vao doi')) {
      return 'sector-hiep-si';
    }

    return 'sector-default';
  };

  // 1. Lắng nghe hồ sơ GLV thời gian thực
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    const unsubscribe = subscribeCatechistProfile(user.uid, (result) => {
      if (result.success) {
        const d = result.data;
        setProfile({
          uid: user.uid,
          idCode: d.idCode || '',
          fullName: d.fullName || 'Giáo lý viên',
          holyName: d.holyName || '',
          gender: d.gender || 'Nam',
          religious: d.religious || 'Giáo lý viên',
          role: d.role || 'catechist',
          username: d.username || '',
          className: d.className || '',
          blockLeader: d.blockLeader || '',
          dateOfBirth: d.dateOfBirth || '',
          phone: d.phone || '',
          email: d.email || '',
          address: d.address || '',
          profession: d.profession || '',
          educationLevel: d.educationLevel || '',
          catechistLevel: d.catechistLevel || 'Dự bị',
          baptismDate: d.baptismDate || '',
          baptismPlace: d.baptismPlace || '',
          confirmationDate: d.confirmationDate || '',
          confirmationPlace: d.confirmationPlace || '',
          notes: d.notes || '',
          isProfileLocked: Boolean(d.isProfileLocked)
        });
      } else {
        setError(result.message);
      }
    });

    return () => unsubscribe && unsubscribe();
  }, [navigate]);

  // GLV bấm trực tiếp vào trường thông tin để sửa
  const handleFieldClick = (fieldKey) => {
    if (profile.isProfileLocked) {
      alert('🔒 Sơ yếu lý lịch của bạn đã bị Quản trị viên khóa. Không thể chỉnh sửa lúc này!');
      return;
    }

    if (!GLV_EDITABLE_FIELDS[fieldKey]) return;

    setGlvEditModal({
      open: true,
      fieldKey,
      value: profile[fieldKey] || ''
    });
  };

  // Xác nhận lưu sửa trường dữ liệu
  const handleSaveField = async (e) => {
    e.preventDefault();
    if (profile.isProfileLocked || !glvEditModal.fieldKey) return;

    setIsSubmittingField(true);
    try {
      const updates = { [glvEditModal.fieldKey]: glvEditModal.value };
      const res = await updateCatechist(profile.uid, updates);
      if (res.success) {
        setProfile((prev) => ({ ...prev, ...updates }));
        setGlvEditModal({ open: false, fieldKey: '', value: '' });
      } else {
        alert('❌ ' + (res.message || 'Lỗi khi cập nhật.'));
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setIsSubmittingField(false);
    }
  };

  // Lắng nghe đơn xin nghỉ
  useEffect(() => {
    if (!currentYearId || !profile.uid) return;
    setIsLoadingLeaveRequests(true);

    const unsubscribe = subscribeLeaveRequestsByCatechist(currentYearId, profile.uid, (requests) => {
      setLeaveRequests(requests);
      setIsLoadingLeaveRequests(false);
    });

    return () => unsubscribe && unsubscribe();
  }, [currentYearId, profile.uid]);

  // Lắng nghe điểm danh cá nhân thời gian thực
  useEffect(() => {
    if (!currentYearId || !profile.uid) return;
    setIsLoadingAttendance(true);

    const unsubscribe = subscribeCatechistAttendanceByUid(currentYearId, profile.uid, (data) => {
      setRawAttendanceMap(data || {});
      setIsLoadingAttendance(false);
    });

    return () => unsubscribe && unsubscribe();
  }, [currentYearId, profile.uid]);

  // Lắng nghe cấu hình ngày họp
  useEffect(() => {
    if (!currentYearId) return;
    const unsubscribe = subscribeMeetingDates(currentYearId, (datesMap) => {
      setMeetingDatesMap(datesMap || {});
    });
    return () => unsubscribe && unsubscribe();
  }, [currentYearId]);

  const loadLeaveRequests = async () => {
    if (!currentYearId || !profile.uid) return;
    setIsLoadingLeaveRequests(true);
    try {
      const requests = await fetchLeaveRequestsByCatechist(currentYearId, profile.uid);
      setLeaveRequests(requests);
    } catch (err) {
      console.error('Lỗi khi tải đơn xin nghỉ:', err);
    } finally {
      setIsLoadingLeaveRequests(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingRequest(null);
    setLeaveForm({
      requestDate: '',
      reason: '',
      absentMass: true,
      absentTeaching: true,
      absentMeeting: false
    });
    setIsLeaveModalOpen(true);
  };

  const handleOpenEditModal = (req) => {
    setEditingRequest(req);
    setLeaveForm({
      requestDate: req.requestDate || '',
      reason: req.reason || '',
      absentMass: req.absentActivities?.includes('mass') || false,
      absentTeaching: req.absentActivities?.includes('teaching') || false,
      absentMeeting: req.absentActivities?.includes('meeting') || false
    });
    setIsLeaveModalOpen(true);
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!leaveForm.requestDate) return alert('Vui lòng chọn ngày xin nghỉ!');
    if (!leaveForm.reason.trim()) return alert('Vui lòng nhập lý do xin nghỉ!');

    const absentActivities = [];
    if (leaveForm.absentMass) absentActivities.push('mass');
    if (leaveForm.absentTeaching) absentActivities.push('teaching');
    if (leaveForm.absentMeeting) absentActivities.push('meeting');

    if (absentActivities.length === 0) {
      return alert('Vui lòng chọn ít nhất 1 buổi xin nghỉ!');
    }

    let derivedBlock = profile.blockLeader;
    if (!derivedBlock && profile.className && allBlocks) {
      Object.keys(allBlocks).forEach((groupCleanId) => {
        const classesInGroup = allBlocks[groupCleanId] || [];
        if (
          classesInGroup.some(
            (c) =>
              c.name === profile.className ||
              c.id === profile.className ||
              profile.className.includes(c.name)
          )
        ) {
          derivedBlock = classesInGroup[0]?.group || '';
        }
      });
    }
    if (!derivedBlock) derivedBlock = profile.className || 'Khác';

    setIsSubmittingLeave(true);
    try {
      if (editingRequest) {
        const res = await updateLeaveRequest(currentYearId, editingRequest.id, {
          requestDate: leaveForm.requestDate,
          reason: leaveForm.reason.trim(),
          absentActivities,
          blockGroup: derivedBlock,
          className: profile.className
        });
        if (res.success) {
          alert('🎉 Đã cập nhật đơn xin nghỉ thành công!');
          setIsLeaveModalOpen(false);
          loadLeaveRequests();
        } else {
          alert('❌ ' + res.message);
        }
      } else {
        const res = await createLeaveRequest(currentYearId, {
          catechistUid: profile.uid,
          holyName: profile.holyName,
          fullName: profile.fullName,
          blockGroup: derivedBlock,
          className: profile.className,
          requestDate: leaveForm.requestDate,
          reason: leaveForm.reason.trim(),
          absentActivities
        });
        if (res.success) {
          alert('🎉 Đã gửi đơn xin nghỉ thành công!');
          setIsLeaveModalOpen(false);
          loadLeaveRequests();
        } else {
          alert('❌ ' + res.message);
        }
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  const historyList = useMemo(() => {
    const dates = Object.keys(rawAttendanceMap).sort().reverse();
    return dates.map((d) => {
      const isMeetingDay = Boolean(meetingDatesMap[d]);
      const rawMeeting = rawAttendanceMap[d]?.meeting || 'Vắng không phép';
      return {
        date: d,
        mass: rawAttendanceMap[d]?.mass || 'Vắng không phép',
        teaching: rawAttendanceMap[d]?.teaching || 'Vắng không phép',
        hasMeeting: isMeetingDay,
        meeting: isMeetingDay ? rawMeeting : '—'
      };
    });
  }, [rawAttendanceMap, meetingDatesMap]);

  const stats = useMemo(() => {
    let massPresent = 0, massExcused = 0, massUnexcused = 0;
    let teachPresent = 0, teachExcused = 0, teachUnexcused = 0;
    let meetPresent = 0, meetExcused = 0, meetUnexcused = 0;

    historyList.forEach((item) => {
      if (item.mass.startsWith('Có lúc')) massPresent++;
      else if (item.mass === 'Vắng có phép') massExcused++;
      else massUnexcused++;

      if (item.teaching.startsWith('Có lúc')) teachPresent++;
      else if (item.teaching === 'Vắng có phép') teachExcused++;
      else teachUnexcused++;

      if (item.hasMeeting) {
        if (item.meeting.startsWith('Có lúc')) meetPresent++;
        else if (item.meeting === 'Vắng có phép') meetExcused++;
        else meetUnexcused++;
      }
    });

    return {
      totalDays: historyList.length,
      massPresent, massExcused, massUnexcused,
      teachPresent, teachExcused, teachUnexcused,
      meetPresent, meetExcused, meetUnexcused
    };
  }, [historyList]);

  const qrValue = profile.idCode || profile.uid;
  const currentModalConfig = GLV_EDITABLE_FIELDS[glvEditModal.fieldKey] || { label: '', type: 'text' };

  // Helper render các ô thông tin với tương tác bấm sửa
  const renderFieldBox = (fieldKey, label, value, icon, fullWidth = false, badgeText = null) => {
    const isEditable = !profile.isProfileLocked && Boolean(GLV_EDITABLE_FIELDS[fieldKey]);
    const isEmpty = !value;

    return (
      <div
        className={`cp-smart-field ${isEditable ? 'editable' : 'readonly'} ${fullWidth ? 'full-span' : ''} ${isEmpty ? 'is-empty' : ''}`}
        onClick={() => isEditable && handleFieldClick(fieldKey)}
        title={isEditable ? `Bấm để sửa ${label}` : undefined}
        role={isEditable ? 'button' : undefined}
        tabIndex={isEditable ? 0 : undefined}
        onKeyDown={(e) => {
          if (isEditable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleFieldClick(fieldKey);
          }
        }}
      >
        <div className="cp-sf-top">
          <span className="cp-sf-icon">{icon}</span>
          <span className="cp-sf-label">{label}</span>
          {isEditable && <span className="cp-sf-pencil">✏️</span>}
        </div>
        <div className="cp-sf-value">
          {value ? (
            <span className="cp-sf-text">{value}</span>
          ) : (
            <span className="cp-sf-placeholder">+ Thêm {label.toLowerCase()}</span>
          )}
          {badgeText && <span className="cp-sf-subbadge">{badgeText}</span>}
        </div>
      </div>
    );
  };

  const initialLetter = (profile.fullName || 'G').trim().charAt(0).toUpperCase();

  return (
    <div className="cp-container">
      {/* HEADER ĐIỀU HƯỚNG */}
      <div className="cp-topbar">
        <div className="cp-topbar-title">
          <span className="cp-topbar-badge">Giáo Phận & Đoàn Thiếu Nhi</span>
          <h2>Hồ Sơ Cá Nhân Giáo Lý Viên</h2>
        </div>
        <button onClick={() => navigate(-1)} className="cp-btn-back">
          <span>←</span> Quay lại
        </button>
      </div>

      {error && <p className="cp-error">{error}</p>}

      {/* THẺ ĐỊNH DANH GLV VÀ QR CODE */}
      <div className="cp-identity-card">
        {/* BANNER TỔNG QUAN */}
        <div className="cp-hero-section">
          <div className="cp-hero-left">
            <div className="cp-avatar-badge" aria-hidden="true">
              <span>{initialLetter}</span>
            </div>

            <div className="cp-hero-names">
              <div className="cp-name-line">
                <span
                  className={`cp-holy-pill ${!profile.isProfileLocked ? 'clickable' : ''}`}
                  onClick={() => handleFieldClick('holyName')}
                  title={!profile.isProfileLocked ? 'Bấm để sửa Tên Thánh' : undefined}
                >
                  ✝️ {profile.holyName || 'Chưa cập nhật'} {!profile.isProfileLocked && '✏️'}
                </span>
                <span className={`cp-gender-badge ${profile.gender === 'Nữ' ? 'female' : 'male'}`}>
                  {profile.gender || 'Nam'}
                </span>
              </div>

              <h1
                className={`cp-fullname-text ${!profile.isProfileLocked ? 'clickable' : ''}`}
                onClick={() => handleFieldClick('fullName')}
                title={!profile.isProfileLocked ? 'Bấm để sửa Họ và Tên' : undefined}
              >
                {profile.fullName} {!profile.isProfileLocked && <small className="cp-mini-edit">✏️</small>}
              </h1>

              {/* HÀNG THẺ TAG PHÂN CÔNG & QUYỀN HẠN */}
              <div className="cp-tags-row">
                <span className={`cp-pill ${getSectorThemeClass(profile.className)}`}>
                  🏫 Lớp: <strong>{profile.className || 'Chưa xếp'}</strong>
                </span>

                {profile.blockLeader && (
                  <span className={`cp-pill ${getSectorThemeClass(profile.blockLeader)}`}>
                    🎖️ Trưởng Khối: <strong>{profile.blockLeader}</strong>
                  </span>
                )}

                <span className={`cp-pill cp-pill-religious ${profile.religious === 'Tu Sĩ' ? 'tu-si' : ''}`}>
                  {profile.religious === 'Tu Sĩ' ? '✝️ Tu Sĩ' : 'Giáo lý viên'}
                </span>

                <span className={`cp-pill cp-pill-role ${profile.role === 'admin' ? 'admin' : ''}`}>
                  {profile.role === 'admin' ? '🛡️ Admin Quản trị' : 'GLV Thành viên'}
                </span>
              </div>

              <div className="cp-hero-actions">
                <button
                  type="button"
                  onClick={() => setIsPasswordModalOpen(true)}
                  className="cp-action-pill password"
                >
                  🔑 Đổi Mật Khẩu
                </button>
              </div>
            </div>
          </div>

          {/* THẺ QR CODE ĐIỂM DANH */}
          <div className="cp-hero-qr-box">
            <div className="cp-qr-header">
              <span className="cp-qr-live-dot" />
              <span>Mã Điểm Danh</span>
            </div>

            <div className="cp-qr-canvas-box">
              {qrValue ? (
                <QRCodeSVG
                  value={qrValue}
                  size={120}
                  level="M"
                  className="cp-qr-canvas"
                />
              ) : (
                <div className="cp-qr-none">Chưa có mã</div>
              )}
            </div>

            <div className="cp-qr-code-strip">
              <span className="cp-qr-code-txt">{profile.idCode || profile.uid || '---'}</span>
            </div>
          </div>
          <div className="cp-open-pill">
            <button
              type="button"
              onClick={() => setIsExpandedCV(!isExpandedCV)}
              className={`cp-action-pill ${isExpandedCV ? 'active' : ''}`}
            >
              {isExpandedCV ? '▲ Thu Gọn Sơ Yếu Lý Lịch' : '📋 Xem Sơ Yếu Lý Lịch'}
            </button>
          </div>
        </div>

        {/* 🌟 VÙNG SƠ YẾU LÝ LỊCH (MẶC ĐỊNH ẨN, KHI BẤM NÚT MỚI MỞ RA) */}
        {isExpandedCV && (
          <div className="cp-cv-dropdown-section">
            <div className="cp-cv-main-header">
              <div className="cp-cv-title-box">
                <span className="cp-cv-title-icon">📜</span>
                <div>
                  <h4>Sơ Yếu Lý Lịch Chi Tiết</h4>
                  <span className="cp-cv-subtitle">Hồ sơ cá nhân và bí tích Giáo Lý Viên</span>
                </div>
              </div>

              {/* 🌟 Ô HIỂN THỊ TRẠNG THÁI SỬA / KHÓA ĐÃ ĐƯỢC CHUYỂN VÀO TRONG ĐÂY */}
              <div className="cp-lock-indicator">
                {profile.isProfileLocked ? (
                  <span className="cp-lock-tag locked" title="Admin đã khóa, bạn chỉ có thể xem thông tin">
                    🔒 Đã khóa hồ sơ
                  </span>
                ) : (
                  <span className="cp-lock-tag unlocked" title="Bạn có thể bấm vào thông tin để cập nhật nhanh">
                    🔓 Chạm thông tin để sửa
                  </span>
                )}
              </div>
            </div>

            <div className="cp-cv-groups-wrapper">
              {/* PHẦN 1: THÔNG TIN CÁ NHÂN & LIÊN HỆ */}
              <div className="cp-section-card">
                <div className="cp-section-header">
                  <span className="cp-section-badge blue">Phần 1</span>
                  <h4>👤 Thông Tin Cá Nhân, Liên Hệ</h4>
                </div>
                <div className="cp-fields-grid">
                  {renderFieldBox('gender', 'Giới tính', profile.gender, '⚧')}
                  {renderFieldBox('dateOfBirth', 'Ngày sinh', profile.dateOfBirth, '🎂')}
                  {renderFieldBox('phone', 'Số điện thoại', profile.phone, '📱')}
                  {renderFieldBox('email', 'Email cá nhân', profile.email, '✉️')}
                  {renderFieldBox('address', 'Địa chỉ cư ngụ', profile.address, '🏠', true)}
                </div>
              </div>

              {/* PHẦN 2: NGHỀ NGHIỆP & CẤP BẬC */}
              <div className="cp-section-card">
                <div className="cp-section-header">
                  <span className="cp-section-badge green">Phần 2</span>
                  <h4>💼 Nghề Nghiệp, Cấp Bậc Phục Vụ</h4>
                </div>
                <div className="cp-fields-grid">
                  {renderFieldBox('profession', 'Nghề nghiệp hiện tại', profile.profession, '💼')}
                  {renderFieldBox('educationLevel', 'Trình độ học vấn', profile.educationLevel, '🎓')}
                  <div className="cp-smart-field readonly">
                    <div className="cp-sf-top">
                      <span className="cp-sf-icon">⭐</span>
                      <span className="cp-sf-label">Cấp bậc GLV</span>
                    </div>
                    <div className="cp-sf-value">
                      <span className="cp-sf-highlight">{profile.catechistLevel || 'Dự bị'}</span>
                      <span className="cp-sf-subbadge">Do BĐH xếp</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* PHẦN 3: BÍ TÍCH KHỞI ĐẦU */}
              <div className="cp-section-card sacraments">
                <div className="cp-section-header">
                  <span className="cp-section-badge gold">Phần 3</span>
                  <h4>🕊️ Hồ Sơ Bí Tích</h4>
                </div>
                <div className="cp-sacraments-layout">
                  {/* BÍ TÍCH RỬA TỘI */}
                  <div className="cp-sacrament-card baptism">
                    <div className="cp-sacrament-title">
                      <span className="cp-sacrament-icon">💧</span>
                      <h5>Bí Tích Rửa Tội (Thanh Tẩy)</h5>
                    </div>
                    <div className="cp-sacrament-fields">
                      {renderFieldBox('baptismDate', 'Ngày Rửa Tội', profile.baptismDate, '📅')}
                      {renderFieldBox('baptismPlace', 'Nơi Rửa Tội (Giáo xứ)', profile.baptismPlace, '⛪')}
                    </div>
                  </div>

                  {/* BÍ TÍCH THÊM SỨC */}
                  <div className="cp-sacrament-card confirmation">
                    <div className="cp-sacrament-title">
                      <span className="cp-sacrament-icon">🕊️</span>
                      <h5>Bí Tích Thêm Sức</h5>
                    </div>
                    <div className="cp-sacrament-fields">
                      {renderFieldBox('confirmationDate', 'Ngày Thêm Sức', profile.confirmationDate, '📅')}
                      {renderFieldBox('confirmationPlace', 'Nơi Thêm Sức (Giáo xứ)', profile.confirmationPlace, '⛪')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MODAL SỬA NHANH MỘT TRƯỜNG DỮ LIỆU */}
      {glvEditModal.open && (
        <div className="cp-modal-overlay">
          <div className="cp-modal-card">
            <div className="cp-modal-top">
              <h3>✏️ Cập Nhật {currentModalConfig.label}</h3>
              <button
                type="button"
                onClick={() => setGlvEditModal({ open: false, fieldKey: '', value: '' })}
                className="cp-modal-close-x"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSaveField}>
              <div className="cp-form-group">
                <label>{currentModalConfig.label}:</label>

                {currentModalConfig.type === 'select' ? (
                  <select
                    value={glvEditModal.value}
                    onChange={(e) => setGlvEditModal({ ...glvEditModal, value: e.target.value })}
                    className="cp-input"
                    autoFocus
                  >
                    {currentModalConfig.options.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={currentModalConfig.type}
                    value={glvEditModal.value}
                    onChange={(e) => setGlvEditModal({ ...glvEditModal, value: e.target.value })}
                    className="cp-input"
                    placeholder={`Nhập ${currentModalConfig.label.toLowerCase()}...`}
                    required={Boolean(currentModalConfig.required)}
                    autoFocus
                  />
                )}
              </div>

              <div className="cp-modal-actions">
                <button
                  type="button"
                  onClick={() => setGlvEditModal({ open: false, fieldKey: '', value: '' })}
                  disabled={isSubmittingField}
                  className="cp-btn-cancel"
                >
                  Hủy Bỏ
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingField}
                  className="cp-btn-submit"
                >
                  {isSubmittingField ? '⏳ Đang lưu...' : '💾 Lưu Lại'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* KHU VỰC TABS CHUYÊN CẦN */}
      <div className="cp-attendance-section">
        <div className="cp-tabs-header">
          <button
            className={`cp-tab-link ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📊 Thống Kê Chuyên Cần
          </button>
          <button
            className={`cp-tab-link ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📜 Lịch Sử Chi Tiết ({historyList.length})
          </button>
          <button
            className={`cp-tab-link ${activeTab === 'leave' ? 'active' : ''}`}
            onClick={() => setActiveTab('leave')}
          >
            📝 Đơn Xin Nghỉ ({leaveRequests.length})
          </button>
        </div>

        {activeTab === 'stats' && (
          <div className="cp-tab-content">
            <div className="cp-stats-grid">
              <div className="cp-stat-card blue">
                <h4>⛪ Đi Lễ Chúa Nhật</h4>
                <p className="present">Có mặt: <strong>{stats.massPresent}</strong></p>
                <p className="excused">Vắng có phép: <strong>{stats.massExcused}</strong></p>
                <p className="unexcused">Vắng không phép: <strong>{stats.massUnexcused}</strong></p>
              </div>

              <div className="cp-stat-card green">
                <h4>📖 Dạy Giáo Lý</h4>
                <p className="present">Có mặt: <strong>{stats.teachPresent}</strong></p>
                <p className="excused">Vắng có phép: <strong>{stats.teachExcused}</strong></p>
                <p className="unexcused">Vắng không phép: <strong>{stats.teachUnexcused}</strong></p>
              </div>

              <div className="cp-stat-card purple">
                <h4>👥 Đi Họp GLV</h4>
                <p className="present">Có mặt: <strong>{stats.meetPresent}</strong></p>
                <p className="excused">Vắng có phép: <strong>{stats.meetExcused}</strong></p>
                <p className="unexcused">Vắng không phép: <strong>{stats.meetUnexcused}</strong></p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="cp-tab-content">
            {isLoadingAttendance ? (
              <p className="cp-loading">⏳ Đang tải lịch sử điểm danh...</p>
            ) : historyList.length === 0 ? (
              <p className="cp-empty">Chưa có lịch sử điểm danh trong niên khóa này.</p>
            ) : (
              <div className="cp-table-wrapper">
                <table className="cp-history-table">
                  <thead>
                    <tr>
                      <th>Ngày</th>
                      <th>⛪ Đi Lễ</th>
                      <th>📖 Dạy Giáo Lý</th>
                      <th>👥 Đi Họp GLV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyList.map((item) => (
                      <tr key={item.date}>
                        <td><strong>{item.date}</strong></td>
                        <td><span className={`cp-status-tag ${getStatusBadgeClass(item.mass)}`}>{item.mass}</span></td>
                        <td><span className={`cp-status-tag ${getStatusBadgeClass(item.teaching)}`}>{item.teaching}</span></td>
                        <td>
                          {item.hasMeeting ? (
                            <span className={`cp-status-tag ${getStatusBadgeClass(item.meeting)}`}>{item.meeting}</span>
                          ) : (
                            <span className="cp-status-tag no-meeting">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'leave' && (
          <div className="cp-tab-content">
            <div className="cp-leave-top-bar">
              <h4>Danh Sách Đơn Xin Nghỉ</h4>
              <button onClick={handleOpenCreateModal} className="cp-btn-create-leave">
                📄 Tạo Đơn Mới
              </button>
            </div>

            {isLoadingLeaveRequests ? (
              <p className="cp-loading">⏳ Đang nạp danh sách đơn xin nghỉ...</p>
            ) : leaveRequests.length === 0 ? (
              <p className="cp-empty">Bạn chưa gửi đơn xin nghỉ nào trong niên khóa này.</p>
            ) : (
              <div className="cp-leave-grid">
                {leaveRequests.map((req) => (
                  <div key={req.id} className="cp-leave-card">
                    <div className="cp-leave-card-head">
                      <span>📅 <strong>{req.requestDate}</strong></span>
                      {req.status === 'pending' && <span className="cp-badge-pending">⏳ Chờ duyệt</span>}
                      {req.status === 'approved' && <span className="cp-badge-approved">✓ Đã duyệt</span>}
                      {req.status === 'rejected' && <span className="cp-badge-rejected">✕ Từ chối</span>}
                    </div>

                    <div className="cp-leave-card-body">
                      <p><strong>Lý do:</strong> {req.reason}</p>
                      {req.reviewerNote && <p className="cp-reviewer-note"><strong>Ghi chú Trưởng khối:</strong> {req.reviewerNote}</p>}
                    </div>

                    {req.status === 'pending' && (
                      <div className="cp-leave-card-actions">
                        <button onClick={() => handleOpenEditModal(req)} className="cp-btn-edit">
                          ✏️ Chỉnh Sửa Đơn
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODAL TẠO / SỬA ĐƠN XIN NGHỈ */}
      {isLeaveModalOpen && (
        <div className="cp-modal-overlay">
          <div className="cp-modal-card">
            <h3>{editingRequest ? '✏️ Chỉnh Sửa Đơn Xin Nghỉ' : '📝 Tạo Đơn Xin Nghỉ Mới'}</h3>
            <form onSubmit={handleLeaveSubmit}>
              <div className="cp-form-group">
                <label>Ngày xin nghỉ (*):</label>
                <input
                  type="date"
                  required
                  value={leaveForm.requestDate}
                  onChange={(e) => setLeaveForm({ ...leaveForm, requestDate: e.target.value })}
                  className="cp-input"
                />
              </div>

              <div className="cp-form-group">
                <label>Các buổi xin vắng (*):</label>
                <div className="cp-checkboxes">
                  <label>
                    <input
                      type="checkbox"
                      checked={leaveForm.absentMass}
                      onChange={(e) => setLeaveForm({ ...leaveForm, absentMass: e.target.checked })}
                    />
                    ⛪ Đi Lễ
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={leaveForm.absentTeaching}
                      onChange={(e) => setLeaveForm({ ...leaveForm, absentTeaching: e.target.checked })}
                    />
                    📖 Dạy Giáo Lý
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={leaveForm.absentMeeting}
                      onChange={(e) => setLeaveForm({ ...leaveForm, absentMeeting: e.target.checked })}
                    />
                    👥 Đi Họp
                  </label>
                </div>
              </div>

              <div className="cp-form-group">
                <label>Lý do vắng mặt (*):</label>
                <textarea
                  rows="3"
                  required
                  placeholder="Nhập lý do chi tiết..."
                  value={leaveForm.reason}
                  onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                  className="cp-textarea"
                />
              </div>

              <div className="cp-modal-actions">
                <button type="button" onClick={() => setIsLeaveModalOpen(false)} disabled={isSubmittingLeave} className="cp-btn-cancel">
                  Hủy Bỏ
                </button>
                <button type="submit" disabled={isSubmittingLeave} className="cp-btn-submit">
                  {isSubmittingLeave ? '⏳ Đang lưu...' : editingRequest ? 'Lưu Thay Đổi' : 'Gửi Đơn'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <PasswordModal isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} />
    </div>
  );
};

const getStatusBadgeClass = (status) => {
  if (!status || status === '—') return 'no-meeting';
  if (status.startsWith('Có lúc')) return 'present';
  if (status === 'Vắng có phép') return 'excused';
  return 'unexcused';
};

export default CatechistProfile;